import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Lock, Users, Mail, MessageSquare, CalendarCheck, Bot, RefreshCw, LogOut, Download, BadgeDollarSign } from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { fetchAdminOverview, adminConfirmTryout, adminUpdateLeadStatus, adminUpdateLeadNote, authExchangeSession, authMe, authLogout } from "../api";

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white rounded-2xl border border-neutral-100 p-5 flex items-center gap-4">
    <div className="h-11 w-11 rounded-xl bg-br-green/10 flex items-center justify-center">
      <Icon className="text-br-green" size={20} />
    </div>
    <div>
      <p className="font-display text-2xl font-extrabold text-[#1A1A1A] leading-none">{value}</p>
      <p className="text-xs text-neutral-500 mt-1">{label}</p>
    </div>
  </div>
);

const toCSV = (rows, cols) => {
  const head = cols.join(",");
  const body = rows.map((r) => cols.map((c) => `"${String(r[c] ?? "").replace(/"/g, '""')}"`).join(",")).join("\n");
  return head + "\n" + body;
};

const download = (filename, csv) => {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
};

const LEAD_STATUSES = [
  ["new", "New Lead"], ["contacted", "Contacted"], ["free_class_booked", "Free Class Booked"], ["attended", "Attended"],
  ["paid_tryout", "Paid Tryout"], ["enrolled", "Enrolled"], ["follow_up", "Follow-Up Needed"], ["lost", "Lost"],
];
const STATUS_COLOR = { new: "bg-blue-50 text-blue-700", contacted: "bg-neutral-100 text-neutral-700", free_class_booked: "bg-br-green/10 text-br-green",
  attended: "bg-br-green/10 text-br-green", paid_tryout: "bg-br-yellow/30 text-[#1A1A1A]", enrolled: "bg-br-green text-white", follow_up: "bg-orange-50 text-orange-700", lost: "bg-red-50 text-red-600" };

const StatusSelect = ({ collection, row, onChange }) => (
  <select
    value={row.lead_status || "new"}
    onChange={async (e) => { await adminUpdateLeadStatus(collection, row.id, e.target.value); onChange(); }}
    data-testid={`lead-status-${row.id}`}
    className={`rounded-full px-2.5 py-1 text-xs font-semibold border-0 cursor-pointer ${STATUS_COLOR[row.lead_status || "new"]}`}
  >
    {LEAD_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
  </select>
);

const NoteCell = ({ collection, row }) => {
  const [val, setVal] = useState(row.note || "");
  const [saved, setSaved] = useState(false);
  const save = async () => {
    if (val === (row.note || "")) return;
    await adminUpdateLeadNote(collection, row.id, val);
    row.note = val; setSaved(true); setTimeout(() => setSaved(false), 1500);
  };
  return (
    <div className="min-w-[180px]">
      <textarea value={val} onChange={(e) => setVal(e.target.value)} onBlur={save} rows={2} placeholder="Add a note…"
        data-testid={`lead-note-${row.id}`} className="w-full text-xs rounded-lg border border-neutral-200 px-2 py-1.5 focus:outline-none focus:border-br-green" />
      {saved && <span className="text-[10px] text-br-green font-semibold">Saved</span>}
    </div>
  );
};

const Table = ({ rows, cols, empty, collection, onChange }) => {
  if (!rows || rows.length === 0) return <p className="text-neutral-500 text-sm py-8 text-center">{empty}</p>;
  return (
    <div className="overflow-x-auto rounded-xl border border-neutral-100">
      <table className="w-full text-sm">
        <thead className="bg-neutral-50 text-neutral-500">
          <tr>{collection && <th className="text-left font-semibold px-4 py-3">Status</th>}{cols.map((c) => <th key={c} className="text-left font-semibold px-4 py-3 capitalize whitespace-nowrap">{c.replace(/_/g, " ")}</th>)}{collection && <th className="text-left font-semibold px-4 py-3">Notes</th>}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-t border-neutral-100 hover:bg-neutral-50/50">
              {collection && <td className="px-4 py-3 align-top"><StatusSelect collection={collection} row={r} onChange={onChange} /></td>}
              {cols.map((c) => (
                <td key={c} className="px-4 py-3 text-neutral-700 align-top max-w-xs">
                  {c === "created_at" && r[c] ? new Date(r[c]).toLocaleString() : String(r[c] ?? "—")}
                </td>
              ))}
              {collection && <td className="px-4 py-3 align-top"><NoteCell collection={collection} row={r} /></td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const AdminDashboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [user, setUser] = useState(location.state?.user || null);
  const [checking, setChecking] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(location.state?.error || "");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await fetchAdminOverview();
      setData(data);
    } catch (e) {
      if (e.response?.status === 401 || e.response?.status === 403) setUser(null);
      setError(e.response?.data?.detail || "Unable to load the dashboard. Please sign in again.");
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  // Exchange the Google session_id from the URL fragment (one-time), then verify the server session.
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const run = async () => {
      const hash = location.hash || "";
      if (hash.includes("session_id=")) {
        const sid = new URLSearchParams(hash.replace(/^#/, "")).get("session_id");
        window.history.replaceState(null, "", location.pathname);
        try {
          const { data } = await authExchangeSession(sid);
          setUser(data);
        } catch (e) {
          setError(e.response?.data?.detail || "Google sign-in failed. Please try again.");
          setChecking(false);
          return;
        }
      } else if (!user) {
        try {
          const { data } = await authMe();
          setUser(data);
        } catch {
          setChecking(false);
          return;
        }
      }
      setChecking(false);
      load();
    };
    run();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (user && !data && !checking && !loading && !error) load();
    // eslint-disable-next-line
  }, [user, checking]);

  const signIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await authLogout(); } catch { /* ignore */ }
    setUser(null); setData(null);
    navigate("/admin", { replace: true });
  };

  if (checking) {
    return <div className="pt-24 min-h-screen flex items-center justify-center text-neutral-500" data-testid="admin-checking">Checking sign-in…</div>;
  }

  if (!user || !data) {
    return (
      <div className="pt-24 min-h-screen">
        <div className="max-w-md mx-auto px-5 py-20">
          <div className="bg-white rounded-2xl border border-neutral-200 shadow-xl p-8 sm:p-10 text-center" data-testid="admin-login-card">
            <div className="mx-auto h-14 w-14 rounded-full bg-br-green/10 flex items-center justify-center mb-5">
              <Lock className="text-br-green" size={26} />
            </div>
            <h1 className="font-display text-2xl font-bold text-[#1A1A1A]">Owner Dashboard</h1>
            <p className="text-neutral-600 mt-2">Sign in with the academy's approved Google account to view leads, bookings, $99 tryouts and chat transcripts.</p>
            {error && <p className="text-sm text-red-500 mt-4" data-testid="admin-login-error">{error}</p>}
            {user && !data ? (
              <Button onClick={load} disabled={loading} className="mt-6 w-full h-12 bg-br-green hover:bg-[#00822f] text-white rounded-full font-semibold">
                {loading ? "Loading..." : "Retry"}
              </Button>
            ) : (
              <Button onClick={signIn} data-testid="admin-google-signin-btn" className="mt-6 w-full h-12 bg-[#1A1A1A] hover:bg-black text-white rounded-full font-semibold">
                <svg width="18" height="18" viewBox="0 0 48 48" className="mr-2" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.2-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3l5.7-5.7C34.1 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C40.9 35.4 44 30.1 44 24c0-1.2-.1-2.4-.4-3.5z"/></svg>
                Sign in with Google
              </Button>
            )}
            <p className="text-xs text-neutral-400 mt-4">Only approved owner accounts can access this page.</p>
          </div>
        </div>
      </div>
    );
  }

  const c = data.counts;

  return (
    <div className="pt-24 min-h-screen bg-[#FBFBF9]">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-[#1A1A1A]">Owner Dashboard</h1>
            <p className="text-neutral-500 text-sm mt-1">Leads, bookings, messages & AI chat transcripts · Signed in as <span data-testid="admin-user-email">{user.email}</span></p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={load} className="rounded-full border-neutral-300"><RefreshCw size={15} className="mr-1.5" /> Refresh</Button>
            <Button variant="outline" onClick={logout} className="rounded-full border-neutral-300"><LogOut size={15} className="mr-1.5" /> Log out</Button>
          </div>
        </div>

        <div className="mt-7 grid grid-cols-2 lg:grid-cols-6 gap-4">
          <StatCard icon={BadgeDollarSign} label="$99 Tryouts" value={c.tryouts ?? 0} />
          <StatCard icon={Users} label="Leads" value={c.leads} />
          <StatCard icon={Mail} label="Newsletter" value={c.newsletter} />
          <StatCard icon={MessageSquare} label="Messages" value={c.contacts} />
          <StatCard icon={CalendarCheck} label="Trial Bookings" value={c.trials} />
          <StatCard icon={Bot} label="Chat Sessions" value={c.chat_sessions} />
        </div>

        <div className="mt-8 bg-white rounded-2xl border border-neutral-100 p-5 sm:p-7">
          <Tabs defaultValue="tryouts">
            <TabsList className="flex flex-wrap h-auto">
              <TabsTrigger value="tryouts" data-testid="admin-tab-tryouts">$99 Tryouts</TabsTrigger>
              <TabsTrigger value="trials">Trial Bookings</TabsTrigger>
              <TabsTrigger value="leads">Leads</TabsTrigger>
              <TabsTrigger value="newsletter">Newsletter</TabsTrigger>
              <TabsTrigger value="contacts">Messages</TabsTrigger>
              <TabsTrigger value="chat_leads" data-testid="admin-tab-chat-leads">Chat Leads</TabsTrigger>
              <TabsTrigger value="chats">Chat Transcripts</TabsTrigger>
            </TabsList>

            <TabsContent value="tryouts" className="mt-5">
              <div className="flex justify-between items-center mb-3 gap-3 flex-wrap">
                <p className="text-xs text-neutral-500">Payments are manual (Venmo / PayPal / Zelle). Check your account for the reference code, then mark as paid.</p>
                <Button variant="outline" size="sm" onClick={() => download("tryouts.csv", toCSV(data.tryouts || [], ["reference","name","email","phone","program","preferred_time","status","marketing_consent","created_at"]))} className="rounded-full border-neutral-300"><Download size={14} className="mr-1.5" /> Export CSV</Button>
              </div>
              {(data.tryouts || []).length === 0 ? (
                <p className="text-neutral-500 text-sm py-8 text-center">No $99 tryout registrations yet.</p>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-neutral-100">
                  <table className="w-full text-sm">
                    <thead className="bg-neutral-50 text-neutral-500">
                      <tr>{["lead status","reference","name","email","phone","program","preferred time","payment","received","action","notes"].map((h) => <th key={h} className="text-left font-semibold px-4 py-3 capitalize whitespace-nowrap">{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {data.tryouts.map((t) => (
                        <tr key={t.id} className="border-t border-neutral-100" data-testid={`tryout-row-${t.reference}`}>
                          <td className="px-4 py-3"><StatusSelect collection="tryouts" row={t} onChange={load} /></td>
                          <td className="px-4 py-3 font-mono text-xs">{t.reference}</td>
                          <td className="px-4 py-3">{t.name}</td>
                          <td className="px-4 py-3">{t.email}</td>
                          <td className="px-4 py-3">{t.phone}</td>
                          <td className="px-4 py-3">{t.program}</td>
                          <td className="px-4 py-3">{t.preferred_time || "—"}</td>
                          <td className="px-4 py-3">
                            <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${t.status === "paid" ? "bg-br-green/10 text-br-green" : t.status === "payment_claimed" ? "bg-br-yellow/30 text-[#1A1A1A]" : "bg-neutral-100 text-neutral-600"}`}>
                              {t.status === "paid" ? "Paid" : t.status === "payment_claimed" ? "Visitor says paid — verify" : "Pending payment"}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">{t.created_at ? new Date(t.created_at).toLocaleString() : ""}</td>
                          <td className="px-4 py-3">
                            {t.status !== "paid" && (
                              <Button size="sm" data-testid={`confirm-paid-${t.reference}`} onClick={async () => { await adminConfirmTryout(t.id); load(); }} className="rounded-full bg-br-green hover:bg-[#00822f] text-white h-8 text-xs">Mark paid</Button>
                            )}
                          </td>
                          <td className="px-4 py-3 align-top"><NoteCell collection="tryouts" row={t} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>

            <TabsContent value="trials" className="mt-5">
              <div className="flex justify-end mb-3">
                <Button variant="outline" size="sm" onClick={() => download("trial-bookings.csv", toCSV(data.trials, ["lead_status","name","parent_name","student_age","email","phone","program","preferred_class","preferred_date","notes","created_at"]))} className="rounded-full border-neutral-300"><Download size={14} className="mr-1.5" /> Export CSV</Button>
              </div>
              <Table rows={data.trials} cols={["name","parent_name","student_age","email","phone","program","preferred_class","preferred_date","sms_consent","created_at"]} empty="No trial bookings yet." collection="trials" onChange={load} />
            </TabsContent>

            <TabsContent value="leads" className="mt-5">
              <div className="flex justify-end mb-3">
                <Button variant="outline" size="sm" onClick={() => download("leads.csv", toCSV(data.leads, ["email","source","created_at"]))} className="rounded-full border-neutral-300"><Download size={14} className="mr-1.5" /> Export CSV</Button>
              </div>
              <Table rows={data.leads} cols={["email","source","created_at"]} empty="No leads yet." />
            </TabsContent>

            <TabsContent value="newsletter" className="mt-5">
              <div className="flex justify-end mb-3">
                <Button variant="outline" size="sm" onClick={() => download("newsletter.csv", toCSV(data.newsletter, ["email","name","created_at"]))} className="rounded-full border-neutral-300"><Download size={14} className="mr-1.5" /> Export CSV</Button>
              </div>
              <Table rows={data.newsletter} cols={["email","name","created_at"]} empty="No newsletter subscribers yet." />
            </TabsContent>

            <TabsContent value="contacts" className="mt-5">
              <div className="flex justify-end mb-3">
                <Button variant="outline" size="sm" onClick={() => download("messages.csv", toCSV(data.contacts, ["name","email","phone","message","created_at"]))} className="rounded-full border-neutral-300"><Download size={14} className="mr-1.5" /> Export CSV</Button>
              </div>
              <Table rows={data.contacts} cols={["name","email","phone","message","created_at"]} empty="No messages yet." collection="contacts" onChange={load} />
            </TabsContent>

            <TabsContent value="chat_leads" className="mt-5">
              <div className="flex justify-end mb-3">
                <Button variant="outline" size="sm" onClick={() => download("chat-leads.csv", toCSV(data.chat_leads || [], ["lead_status","name","email","phone","child_age","program","preferred_time","source","created_at"]))} className="rounded-full border-neutral-300"><Download size={14} className="mr-1.5" /> Export CSV</Button>
              </div>
              <Table rows={data.chat_leads || []} cols={["name","email","phone","child_age","program","preferred_time","source","created_at"]} empty="No chat leads yet." collection="chat_leads" onChange={load} />
            </TabsContent>

            <TabsContent value="chats" className="mt-5">
              {data.chat_sessions.length === 0 ? (
                <p className="text-neutral-500 text-sm py-8 text-center">No chat conversations yet.</p>
              ) : (
                <div className="space-y-4">
                  {data.chat_sessions.map((s, i) => (
                    <details key={i} className="rounded-xl border border-neutral-100 bg-neutral-50/50 overflow-hidden">
                      <summary className="cursor-pointer px-4 py-3 font-medium text-sm text-[#1A1A1A] flex items-center gap-2">
                        <Bot size={15} className="text-br-green" /> Conversation ({s.messages.length} messages)
                        <span className="text-neutral-400 font-normal">· {s.messages[0]?.created_at ? new Date(s.messages[0].created_at).toLocaleString() : ""}</span>
                      </summary>
                      <div className="px-4 pb-4 space-y-2">
                        {s.messages.map((m, j) => (
                          <div key={j} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                            <div className={`max-w-[80%] rounded-xl px-3.5 py-2 text-sm ${m.role === "user" ? "bg-br-green text-white" : "bg-white border border-neutral-200 text-neutral-700"}`}>
                              {m.content}
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
